import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./PersonalNameItem.module.css";
import { capitalizeName } from "components/shared/capitalizeName";
import { useTranslation } from "react-i18next";

export function PersonalNameItem({
  label,
  name = { firstName: "", middleName: "", lastName: "" },
  onSave,
  onToggleEdit,
  isEditing,
  error = {},
}) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState(name.firstName || "");
  const [middleName, setMiddleName] = useState(name.middleName || "");
  const [localError, setLocalError] = useState("");

  useEffect(() => {
    setFirstName(name.firstName || "");
    setMiddleName(name.middleName || "");
  }, [name.firstName, name.middleName]);

  const handleOnSave = () => {
    if (firstName.length > 20 || middleName.length > 20) {
      setLocalError(t("First name and middle name characters"));
      return;
    }
    setLocalError("");
    onSave({ firstName, middleName });
  };

  const handleFirstNameChange = (e) => {
    const value = e.target.value;
    setFirstName(value);
    if (value.length <= 20) {
      setLocalError("");
    } else {
      setLocalError(t("First name and middle name characters"));
    }
  };

  const handleMiddleNameChange = (e) => {
    const value = e.target.value;
    setMiddleName(value);
    if (value.length <= 20) {
      setLocalError("");
    } else {
      setLocalError(t("First name and middle name characters"));
    }
  };

  const handleReset = () => {
    onToggleEdit();
    setFirstName(name.firstName || "");
    setMiddleName(name.middleName || "");
  };

  const hasErrors = localError || error.firstName || error.middleName;

  return (
    <div className={styles.container}>
      {isEditing ? (
        <div className={styles.editContainer}>
          <div className={styles.inputContainer}>
            <input
              id={`${label}-firstName`}
              className={
                localError || error.firstName ? styles.inputError : styles.input
              }
              type="text"
              value={firstName}
              onChange={handleFirstNameChange}
              placeholder={t("First Name")}
            />
            <input
              id={`${label}-middleName`}
              className={
                localError || error.middleName
                  ? styles.inputError
                  : styles.input
              }
              type="text"
              value={middleName}
              onChange={handleMiddleNameChange}
              placeholder={t("Middle Name")}
            />
          </div>
          <div className={styles.btnContainer}>
            <button className={styles.editBtn} onClick={handleReset}>
              {t("Cancel")}
            </button>
            <button
              className={classNames(styles.editBtn, styles.saveBtn)}
              onClick={handleOnSave}
              disabled={!!hasErrors}
            >
              {t("Save")}
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.editContainer}>
          <div className={styles.label}>
            {label}:
            <span className={styles.info}>
              {capitalizeName(name.firstName)} {capitalizeName(name.middleName)}{" "}
              {capitalizeName(name.lastName)}
            </span>
          </div>
          <div className={styles.btnContainer}>
            <button className={styles.editBtn} onClick={onToggleEdit}>
              {t("Edit")}
            </button>
          </div>
        </div>
      )}

      <div className={styles.errorContainer}>
        {localError && <span className={styles.error}>{localError}</span>}
        {error && typeof error === "string" && (
          <span className={styles.error}>{error}</span>
        )}
      </div>
    </div>
  );
}

PersonalNameItem.propTypes = {
  label: PropTypes.string,
  name: PropTypes.shape({
    firstName: PropTypes.string,
    middleName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  onSave: PropTypes.func.isRequired,
  onToggleEdit: PropTypes.func.isRequired,
  isEditing: PropTypes.bool.isRequired,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

export default PersonalNameItem;
